import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import AnimalSymbol from "@/components/AnimalSymbol";
import PedigreeTree from "@/components/PedigreeTree";
import { emptyParts } from "@/lib/quality";
import { CONDITION_FIELDS } from "@/lib/conditions";
import { formatUS } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Printable 5-generation pedigree certificate.
 *
 * Uses the `<PedigreeTree compact>` variant (same ancestor alignment as the
 * web view) at NATIVE Letter-landscape dimensions — no JS scale/zoom.
 * Avoids Safari's print-mode quirks where `transform: scale` and `zoom`
 * paginate inconsistently. The tree is hand-tuned to land within ~990×740
 * usable px so it fits on a single 8.5×11 in landscape page in both Chrome
 * and Safari.
 */
export default function PrintPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const template = sp.get("template") || "akc";
  // Generations: 3 is the Safari-safe default (large readable cards, fits
  // landscape Letter without scaling). 5 is the dense version intended for
  // Chrome/Firefox where pagination is well-behaved.
  const requestedGen = parseInt(sp.get("gen") || "3", 10);
  const generations = [3, 4, 5].includes(requestedGen) ? requestedGen : 3;
  const [animal, setAnimal] = useState(null);
  const [pedigree, setPedigree] = useState(null);

  useEffect(() => {
    // Strip the Emergent preview badge so it doesn't push the document
    // onto a second printed page.
    const el = document.getElementById("emergent-badge");
    if (el) {
      el.style.setProperty("display", "none", "important");
      return () => el.style.removeProperty("display");
    }
  }, []);

  useEffect(() => {
    (async () => {
      const a = await axios.get(`${API}/animals/${id}`);
      setAnimal(a.data);
      const p = await axios.get(`${API}/animals/${id}/pedigree?generations=${generations}`);
      setPedigree(p.data);
    })();
  }, [id, generations]);

  useEffect(() => {
    if (animal && pedigree) {
      const t = setTimeout(() => window.print(), 700);
      return () => clearTimeout(t);
    }
  }, [animal, pedigree]);

  const ancestors = useMemo(() => {
    if (!pedigree) return {};
    const out = {};
    Object.entries(pedigree.ancestors).forEach(([k, v]) => { out[parseInt(k, 10)] = v; });
    return out;
  }, [pedigree]);

  if (!animal || !pedigree) return <div className="p-10">Preparing pedigree…</div>;

  const isAkc = template !== "fci";
  const today = formatUS(new Date().toISOString().slice(0, 10));
  const conds = CONDITION_FIELDS.filter((f) => (animal[f.key] || "").trim());

  return (
    <div className={`print-doc template-${template}`} data-testid="print-doc" data-gen={generations}>
      <div className="print-scale">
        <div className="print-header">
          <div className="title-strip">
            <span className="brand">Vitality Pedigree</span>
            <span className="brand-sub">Structure &amp; Medical · {pedigree.generations}-generation pedigree</span>
            <span className="title-meta">{isAkc ? "AKC-style" : "FCI-style"} · {today}</span>
          </div>
        </div>

        <div className="subject-strip">
          <AnimalSymbol parts={animal.parts || emptyParts()} size={56} />
          <div className="subject-line">
            <div className="subject-name">{animal.name}</div>
            <div className="subject-meta-row">
              <span><label>Breed</label>{animal.species_or_breed || "—"}</span>
              <span><label>Sex</label>{animal.sex}</span>
              <span><label>DOB</label>{formatUS(animal.dob) || "—"}</span>
              <span><label>Reg #</label>{animal.registration || "—"}</span>
              {animal.title && <span><label>Title</label>{animal.title}</span>}
            </div>
            {conds.length > 0 && (
              <div className="subject-meta-row">
                {conds.map((f) => (
                  <span key={f.key}>
                    <label style={{ color: f.color }}>{f.label}</label>
                    {animal[f.key]}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="print-tree-wrap">
          <PedigreeTree
            generations={pedigree.generations}
            ancestors={ancestors}
            selectedIndex={null}
            onSelect={() => {}}
            compact={generations !== 3}
            printStretched={generations === 3}
          />
        </div>

        <div className="print-footer">
          <span>Generated by Vitality Pedigree on {today}</span>
          <span className="mono">Template: {template.toUpperCase()}</span>
        </div>
      </div>
    </div>
  );
}
